import { sfGet, sfPost, sfPatch } from "./salesforceApi";
import { fromSalesforceProject } from "../schema/projectSchema";
import { validateProject } from "../utils/validation";
import { getEnv } from "../config/getEnv";

// Service layer that encapsulates Salesforce Apex REST calls for project data.
// Adjust endpoint paths as needed to match backend Apex REST routes.

const PROJECT_RESOURCE = getEnv("REACT_APP_SF_PROJECT_RESOURCE") || "/Project";

export async function fetchProject(projectId) {
  if (!projectId) {
    throw new Error("fetchProject requires a projectId");
  }
  try {
    const data = await sfGet(`${PROJECT_RESOURCE}`, { id: String(projectId) });
    // API may return a single record or an array of records in data
    // fromSalesforceProject expects wrapped format: { data: [record] }
    const wrapped = Array.isArray(data) ? { data } : { data: [data] };
    return fromSalesforceProject(wrapped);
  } catch (err) {
    throw err; // already normalized by salesforceApi
  }
}

export async function fetchAllProjects() {
  try {
    const data = await sfGet(`${PROJECT_RESOURCE}`, { id: "all" });
    return data;
  } catch (err) {
    throw err;
  }
}

export async function searchProjects(searchKey) {
  const key = String(searchKey || "").trim();
  if (key.length === 0) return [];
  try {
    const data = await sfGet(`${PROJECT_RESOURCE}`, { searchkey: key });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    // surface empty list on failure; caller may show error UI elsewhere
    return [];
  }
}

export async function createProject(project) {
  const errors = validateProject(project);
  if (errors.length > 0) {
    const message = errors[0]?.message || "Validation failed";
    const error = new Error(message);
    error.validationErrors = errors;
    throw error;
  }
  try {
    const data = await sfPost(PROJECT_RESOURCE, project);
    return data;
  } catch (err) {
    throw err;
  }
}

export async function updateProject(projectId, project) {
  if (!projectId) {
    throw new Error("updateProject requires a projectId");
  }
  const errors = validateProject(project);
  if (errors.length > 0) {
    const message = errors[0]?.message || "Validation failed";
    const error = new Error(message);
    error.validationErrors = errors;
    throw error;
  }
  try {
    // Use PATCH as requested by API contract
    const data = await sfPatch(`${PROJECT_RESOURCE}`, project, {
      params: { id: String(projectId) },
    });
    return data;
  } catch (err) {
    throw err;
  }
}

// Partial update without full project validation; accepts either UI-shaped payload
// or a Salesforce field map (e.g., { Id, Name, Short_Project_Name__c, ... }).
export async function patchProject(projectId, partial) {
  if (!projectId) {
    throw new Error("patchProject requires a projectId");
  }
  try {
    const data = await sfPatch(`${PROJECT_RESOURCE}`, partial, {
      params: { id: String(projectId) },
    });
    return data;
  } catch (err) {
    throw err;
  }
}
